import React from 'react';
import {Route, useRouteMatch} from 'react-router-dom';
import loadable from '@loadable/component';
import {useTranslation} from 'react-i18next';
import {useQuery, gql} from '@apollo/client';
import {EzManageNavSwitch} from './components/EzManageNav';
// Helper Components
import AnalyticsTracker from './components/AnalyticsTracker';
import EzManageRoute, {ezManageRouteFragment} from './components/EzManageRoute';
import EzManageRouteNoAuth from './components/EzManageRoute/EzManageRouteNoAuth';
import RedirectRoute from './components/RedirectRoute';
import Intercom, {intercomFragment} from './components/Intercom';
import FullStory, {fullStoryFragment} from './components/FullStory';
// Components
import GeneralError from './components/GeneralError';
import {
  ADD_NEW_CATERING_PARTNER_ACCOUNT_PATH,
  ADD_NEW_STORE_PATH,
  ADD_NEW_STORE_ACH_FORM_PATH,
  ADD_NEW_STORE_LISTING_INFORMATION_PATH,
  BULK_EDIT_STORE_SETTINGS_PATH,
  CLUB_SODA_PATH,
  DELIVERY_PERFORMANCE_REPORTING_PATH,
  DIRECT_ENTRY_PATH,
  DRAFT_AND_QUOTE_INDEX_PATH,
  EDIT_DAILY_CAPACITY_PATH,
  EDIT_LEAD_TIME_PATH,
  EDIT_OPERATING_SCHEDULES_PATH,
  EDIT_STORE_DETAILS_PATH,
  EDIT_TEMPORARY_CLOSURES_PATH,
  EZ_DISPATCH_PREFERENCES_PATH,
  EZ_ORDERING_MARKETING_PATH,
  HEALTH_CHECK_PATH,
  INTEGRATION_CREDENTIALS_PATH,
  LEGACY_CLUB_SODA_PATH,
  LEGACY_DRAFT_AND_QUOTE_INDEX_PATH,
  MARK_ORDER_READY_FOR_PICKUP_PATH,
  MENU_PERFORMANCE_PATH,
  ONBOARDING_PATH,
  OPERATIONAL_METRICS_PATH,
  OPERATIONAL_METRICS_HELP_CENTER_PATH,
  ORDER_DETAILS_PATH,
  ORDER_MESSAGE_PATH,
  ORDER_ENTRY_MODIFICATION_PATH,
  ORDER_ENTRY_NEW_PATH,
  ORDER_ENTRY_PATH,
  ORDER_ENTRY_REVIEW_PATH,
  ORDER_INDEX_PATH,
  ORDER_CANCELLED_PATH,
  ORDER_COMPLETED_PATH,
  ORDER_REJECTED_PATH,
  PAYMENT_DETAILS_PATH,
  PAYMENTS_PATH,
  PERFORMANCE_GROUP_PATH,
  PERFORMANCE_PATH,
  PERFORMANCE_STORE_PATH,
  PERFORMANCE_METRICS_PATH,
  PROMOTIONS_PATH,
  PROMOTION_NEW_PATH,
  PROMOTION_DETAILS_PATH,
  PROMO_DETAIL_REDEMPTIONS_PATH,
  QUOTE_OR_ORDER_PATH,
  RANKINGS_PATH,
  REVIEWS_PATH,
  SETTINGS_ACCOUNT_PATH,
  SETTINGS_BULK_EDIT_LEAD_TIMES_PATH,
  SETTINGS_BULK_EDIT_OPERATING_SCHEDULES_PATH,
  SETTINGS_BULK_EDIT_TEMPORARY_CLOSURES_PATH,
  SETTINGS_EDIT_ACCOUNT_ACCESS_PATH,
  SETTINGS_EDIT_ACCOUNT_CONTACT_METHODS_PATH,
  SETTINGS_EDIT_ACCOUNT_DEMOGRAPHIC_DATA_PATH,
  SETTINGS_EDIT_ACCOUNT_NOTIFICATIONS_PATH,
  SETTINGS_EDIT_ACCOUNT_PERMISSIONS_PATH,
  SETTINGS_PAYMENT,
  SETTINGS_PERSONAL_STORE_GROUP_PATH,
  SETTINGS_PERSONAL_STORE_GROUPS_PATH,
  SETTINGS_SERVICES_PATH,
  SETTINGS_STORE_DETAILS_PATH,
  SETTINGS_STORE_PATH,
  SETTINGS_USERS_PATH,
  SUBWAY_URL,
  TAX_INFO_SETTINGS,
  TRAINING_RESOURCES_PATH,
  RELIABILITY_ROCKSTAR_PATH,
  MENU_PATH,
  SETTINGS_BULK_EDIT_HOLIDAY_CLOSURES_PATH,
  EDIT_HOLIDAY_CLOSURES_PATH,
  EDIT_POLICIES_PATH,
  SETTINGS_BULK_EDIT_DAILY_CAPACITY_PATH,
  RANKINGS_STORE_PATH,
  OPERATIONAL_METRICS_STORE_PATH,
  WORKRAMP_PATH,
  SPONSORED_LISTING_LANDING_PATH,
  SPONSORED_LISTING_CAMPAIGNS_PATH,
  SPONSORED_LISTING_CAMPAIGN_DETAILS_PATH,
  SPONSORED_LISTING_CAMPAIGN_NEW_PATH,
  SPONSORED_LISTING_CAMPAIGN_GROUP_EDIT_PATH,
  MARKETING_ACTIVITY_PATH,
  PROMO_NEW_PATH,
  PROMOS_PATH,
  PROMO_DETAILS_PATH,
  MARKETING_TOOLS_PATH,
} from './paths';
import ListingInformationPage from './pages/AddNewStore/ListingInformationPage';
import ACHFormPage from './pages/AddNewStore/ACHFormPage';

const AddCateringPartnerAccount = loadable(() => import('./pages/AddCateringPartnerAccount'));
const BulkEditDailyCapacity = loadable(() => import('./pages/BulkEditDailyCapacity'));
const BulkEditHolidayClosures = loadable(() => import('./pages/BulkEditHolidayClosures'));
const BulkEditLeadTimes = loadable(() => import('./pages/BulkEditLeadTimes'));
const BulkEditOperatingSchedules = loadable(() => import('./pages/BulkEditOperatingSchedules'));
const BulkEditTemporaryClosures = loadable(() => import('./pages/BulkEditTemporaryClosures'));
const BulkEditStoreSettings = loadable(() => import('./pages/BulkEditStoreSettingsIndex'));
const CateringPartnerAccountSettings = loadable(
  () => import('./pages/CateringPartnerAccountSettings/index')
);
const ClubSoda = loadable(() => import('./pages/ClubSoda'));
const CreateSponsoredListingCampaign = loadable(
  () => import('./pages/CreateSponsoredListingCampaign')
);
const CreatePromo = loadable(() => import('./pages/CreatePromo'));
const DeliveryPerformanceReporting = loadable(() => import('./pages/DeliveryPerformanceReporting'));
const EditAccountContactMethods = loadable(() => import('./pages/EditAccountContactMethods'));
const EditDemographicData = loadable(() => import('./pages/EditDemographicData'));
const EditDailyCapacity = loadable(() => import('./pages/EditDailyCapacity'));
const EditHolidayClosures = loadable(() => import('./pages/EditHolidayClosures'));
const EditLeadTime = loadable(() => import('./pages/EditLeadTime'));
const EditOperatingSchedules = loadable(() => import('./pages/EditOperatingSchedules'));
const EditPolicies = loadable(() => import('./pages/EditPolicies'));
const EditSponsoredListingCampaignGroup = loadable(
  () => import('./pages/EditSponsoredListingCampaignGroup')
);
const EditStoreDetails = loadable(() => import('./pages/EditStoreDetails'));
const EditTemporaryClosures = loadable(() => import('./pages/EditTemporaryClosures'));
const EzDispatchPreferences = loadable(() => import('./pages/EzDispatchPreferences'));
const EzOrderingMarketing = loadable(() => import('./pages/EzOrderingMarketing'));
const HealthCheck = loadable(() => import('./pages/HealthCheck'));
const SponsoredListingsLanding = loadable(
  () => import('./pages/MarketingTools/SponsoredListingsLanding')
);
const SponsoredListingCampaignDetails = loadable(
  () => import('./pages/MarketingTools/SponsoredListingCampaignDetails')
);
const SponsoredListingCampaigns = loadable(
  () => import('./pages/MarketingTools/SponsoredListingCampaigns')
);
const SponsoredPromos = loadable(() => import('./pages/MarketingTools/SponsoredPromos'));
const SponsoredPromoDetails = loadable(
  () => import('./pages/MarketingTools/SponsoredPromoDetails')
);
const MarketingActivity = loadable(() => import('./pages/MarketingTools/MarketingActivity'));
const MarkOrderReadyForPickup = loadable(
  () => import('./pages/ReadyForPickup/ReadyForPickupStepContainer')
);
const Menu = loadable(() => import('./pages/Menu'));
const MenuPerformance = loadable(() => import('./pages/MenuPerformance'));
const ModificationOrder = loadable(() => import('./pages/ModificationOrder'));
const NewOrder = loadable(() => import('./pages/NewOrder'));
const Onboarding = loadable(() => import('./pages/Onboarding'));
const OperationalMetrics = loadable(() => import('./pages/ReliabilityMetrics/OperationalMetrics'));
const OrderDetails = loadable(() => import('./pages/OrderDetails'));
const OrderEntry = loadable(() => import('./pages/OrderEntry'));
const OrderIndex = loadable(() => import('./pages/OrderIndex'));
const OrderMessage = loadable(() => import('./pages/OrderMessage'));
const Payments = loadable(() => import('./pages/Payments'));
const PaymentDetails = loadable(() => import('./pages/PaymentDetails'));
const Performance = loadable(() => import('./pages/Performance'));
const PerformanceGroup = loadable(() => import('./pages/PerformanceGroup/PerformanceGroup'));
const PerformanceStore = loadable(() => import('./pages/PerformanceStore'));
const PerformanceMetrics = loadable(() => import('./pages/PerformanceMetrics'));
const PersonalStoreGroupDetails = loadable(() => import('./pages/PersonalStoreGroupDetails'));
const Promotions = loadable(() => import('./pages/Promotions'));
const PromotionNew = loadable(() => import('./pages/PromotionNew'));
const PromotionDetails = loadable(() => import('./pages/PromotionDetails'));
const QuoteOrOrderRedirect = loadable(() => import('./pages/QuoteOrOrderRedirect'));
const ReviewOrder = loadable(() => import('./pages/ReviewOrder'));
const Reviews = loadable(() => import('./pages/Reviews'));
const Settings = loadable(() => import('./pages/Settings'));
const StoreDetails = loadable(() => import('./pages/StoreDetails'));
const TaxInfoSettings = loadable(() => import('./pages/TaxInfoSettings'));
const TrainingResources = loadable(() => import('./pages/TrainingResources'));
const Rankings = loadable(() => import('./pages/Rankings'));
const ReliabilityRockstar = loadable(
  () => import('./pages/ReliabilityMetrics/ReliabilityRockstar')
);
const StoreRankingsDetails = loadable(() => import('./pages/RankingsDetails'));
const Subway = loadable(() => import('./pages/Subway'));
const Workramp = loadable(() => import('./pages/Workramp'));

const RoutesQuery = gql`
  query RoutesQuery {
    me {
      id
      ...Intercom
      ...FullStory
      ...EzManageRoute
    }
  }
  ${intercomFragment}
  ${fullStoryFragment}
  ${ezManageRouteFragment}
`;

const authenticatedRoutes = [
  {
    linkId: 'ORDERS',
    exact: true,
    path: [
      ORDER_CANCELLED_PATH,
      DRAFT_AND_QUOTE_INDEX_PATH,
      ORDER_COMPLETED_PATH,
      ORDER_REJECTED_PATH,
      ORDER_INDEX_PATH,
    ],
    component: OrderIndex,
  },
  {
    linkId: 'MARKETING_TOOLS',
    exact: true,
    path: SPONSORED_LISTING_CAMPAIGN_NEW_PATH,
    component: CreateSponsoredListingCampaign,
  },
  {
    linkId: 'MARKETING_TOOLS',
    exact: true,
    path: SPONSORED_LISTING_CAMPAIGN_GROUP_EDIT_PATH,
    component: EditSponsoredListingCampaignGroup,
  },
  {
    linkId: 'MARKETING_TOOLS',
    exact: true,
    path: SPONSORED_LISTING_CAMPAIGNS_PATH,
    component: SponsoredListingCampaigns,
  },
  {
    linkId: 'MARKETING_TOOLS',
    exact: true,
    path: SPONSORED_LISTING_CAMPAIGN_DETAILS_PATH,
    component: SponsoredListingCampaignDetails,
  },
  {
    linkId: 'MARKETING_TOOLS',
    exact: true,
    path: MARKETING_ACTIVITY_PATH,
    component: MarketingActivity,
  },
  {
    linkId: 'MARKETING_TOOLS',
    exact: true,
    path: PROMO_NEW_PATH,
    component: CreatePromo,
  },
  {
    linkId: 'MARKETING_TOOLS',
    exact: true,
    path: PROMO_DETAILS_PATH,
    component: SponsoredPromoDetails,
  },
  {
    linkId: 'MARKETING_TOOLS',
    exact: true,
    path: PROMOS_PATH,
    component: SponsoredPromos,
  },
  {
    linkId: 'MARKETING_TOOLS',
    exact: true,
    path: SPONSORED_LISTING_LANDING_PATH,
    component: SponsoredListingsLanding,
  },
];

const Routes = ({requestUrl, loggedOutListener}) => {
  const {t} = useTranslation();
  const healthCheckMatch = useRouteMatch(HEALTH_CHECK_PATH);

  // Health check views shouldn't ever have a logged in user so just skip the
  // query to avoid error noise
  const shouldSkipQuery = !!healthCheckMatch;
  const {data, loading, error} = useQuery(RoutesQuery, {
    errorPolicy: 'ignore',
    skip: shouldSkipQuery,
  });
  const AuthenticatedRoute = props => {
    return (
      <EzManageRoute
        {...props}
        requestUrl={requestUrl}
        loggedOutListener={loggedOutListener}
        me={data?.me}
        loading={loading}
        error={error}
      />
    );
  };
  return (
    <Intercom me={data?.me}>
      <AnalyticsTracker />
      <EzManageNavSwitch useMessage={t} appName="EZ_MANAGE_UI">
        <Route exact path={HEALTH_CHECK_PATH} render={() => <HealthCheck />} />
        <AuthenticatedRoute
          linkId="MENU_PERFORMANCE"
          exact
          path={MENU_PERFORMANCE_PATH}
          component={MenuPerformance}
        />
        <AuthenticatedRoute
          linkId="ORDERS"
          exact
          path={ORDER_ENTRY_NEW_PATH}
          component={NewOrder}
        />
        <AuthenticatedRoute
          linkId="ORDERS"
          exact
          path={ORDER_ENTRY_MODIFICATION_PATH}
          component={ModificationOrder}
        />
        <AuthenticatedRoute
          linkId="ORDERS"
          exact
          path={ORDER_ENTRY_PATH}
          component={OrderEntry}
          renderNav={false}
        />
        <AuthenticatedRoute
          linkId="ORDERS"
          exact
          path={ORDER_ENTRY_REVIEW_PATH}
          component={ReviewOrder}
        />
        <AuthenticatedRoute
          linkId="ONBOARDING"
          exact
          path={ONBOARDING_PATH}
          component={Onboarding}
        />
        <AuthenticatedRoute linkId="PAYMENTS" exact path={PAYMENTS_PATH} component={Payments} />
        <AuthenticatedRoute
          linkId="PAYMENTS"
          path={PAYMENT_DETAILS_PATH}
          component={PaymentDetails}
        />
        <AuthenticatedRoute
          linkId="PROMOTION_NEW"
          exact
          path={PROMOTION_NEW_PATH}
          component={PromotionNew}
        />
        <AuthenticatedRoute
          linkId="PROMOTIONS"
          exact
          path={PROMOTIONS_PATH}
          component={Promotions}
        />
        <AuthenticatedRoute
          linkId="PROMOTIONS"
          exact
          path={PROMOTION_DETAILS_PATH}
          component={PromotionDetails}
        />
        <AuthenticatedRoute
          linkId="PROMOTIONS"
          exact
          path={PROMO_DETAIL_REDEMPTIONS_PATH}
          component={PromotionDetails}
        />
        <AuthenticatedRoute linkId="RANKINGS" exact path={RANKINGS_PATH} component={Rankings} />
        <AuthenticatedRoute
          linkId="RANKINGS"
          exact
          path={RANKINGS_STORE_PATH}
          component={StoreRankingsDetails}
        />
        <AuthenticatedRoute
          linkId="PERFORMANCE"
          exact
          path={PERFORMANCE_PATH}
          component={Performance}
        />
        <AuthenticatedRoute
          linkId="PERFORMANCE"
          exact
          path={PERFORMANCE_STORE_PATH}
          component={PerformanceStore}
        />
        <AuthenticatedRoute
          linkId="PERFORMANCE"
          path={PERFORMANCE_GROUP_PATH}
          component={PerformanceGroup}
        />
        <AuthenticatedRoute
          linkId="PERFORMANCE"
          exact
          path={PERFORMANCE_METRICS_PATH}
          component={PerformanceMetrics}
        />
        <AuthenticatedRoute
          linkId="ORDERS"
          exact
          path={ORDER_MESSAGE_PATH}
          component={OrderMessage}
          requireFullAccess={false}
        />
        <AuthenticatedRoute
          linkId="ORDERS"
          path={ORDER_DETAILS_PATH}
          component={OrderDetails}
          // Allow this page to be viewed by "anonymous" aka nonFullAccess contacts
          // by way of ez-rails path /cp/vo/<order.url_id>?cu=<contact.url_id>
          requireFullAccess={false}
        />
        <AuthenticatedRoute linkId="REVIEWS" exact path={REVIEWS_PATH} component={Reviews} />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={EDIT_DAILY_CAPACITY_PATH}
          component={EditDailyCapacity}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={EDIT_STORE_DETAILS_PATH}
          component={EditStoreDetails}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={EDIT_TEMPORARY_CLOSURES_PATH}
          component={EditTemporaryClosures}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={EDIT_HOLIDAY_CLOSURES_PATH}
          component={EditHolidayClosures}
        />
        <AuthenticatedRoute linkId="SETTINGS" path={EDIT_POLICIES_PATH} component={EditPolicies} />
        <AuthenticatedRoute
          linkId="EZ_DISPATCH_SETTINGS"
          path={EZ_DISPATCH_PREFERENCES_PATH}
          component={EzDispatchPreferences}
        />
        <AuthenticatedRoute
          linkId="DELIVERY_PERFORMANCE_REPORTING"
          path={DELIVERY_PERFORMANCE_REPORTING_PATH}
          component={DeliveryPerformanceReporting}
        />
        <AuthenticatedRoute
          linkId="GET_MORE_ORDERS"
          path={EZ_ORDERING_MARKETING_PATH}
          component={EzOrderingMarketing}
        />
        <AuthenticatedRoute linkId="SETTINGS" path={SETTINGS_ACCOUNT_PATH} component={Settings} />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={ADD_NEW_CATERING_PARTNER_ACCOUNT_PATH}
          component={AddCateringPartnerAccount}
          exact
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={ADD_NEW_STORE_LISTING_INFORMATION_PATH}
          component={ListingInformationPage}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={ADD_NEW_STORE_ACH_FORM_PATH}
          component={ACHFormPage}
        />
        <AuthenticatedRoute linkId="SETTINGS" path={ADD_NEW_STORE_PATH} component={Settings} />
        <AuthenticatedRoute linkId="SETTINGS" path={SETTINGS_USERS_PATH} component={Settings} />
        <AuthenticatedRoute linkId="SETTINGS" path={SETTINGS_SERVICES_PATH} component={Settings} />
        <AuthenticatedRoute linkId="SETTINGS" path={SETTINGS_PAYMENT} component={Settings} />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={BULK_EDIT_STORE_SETTINGS_PATH}
          component={BulkEditStoreSettings}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_STORE_PATH}
          component={Settings}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={INTEGRATION_CREDENTIALS_PATH}
          component={Settings}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_EDIT_ACCOUNT_CONTACT_METHODS_PATH}
          component={EditAccountContactMethods}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_EDIT_ACCOUNT_DEMOGRAPHIC_DATA_PATH}
          component={EditDemographicData}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_EDIT_ACCOUNT_ACCESS_PATH}
          component={CateringPartnerAccountSettings}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_EDIT_ACCOUNT_PERMISSIONS_PATH}
          component={CateringPartnerAccountSettings}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_EDIT_ACCOUNT_NOTIFICATIONS_PATH}
          component={CateringPartnerAccountSettings}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_PERSONAL_STORE_GROUP_PATH}
          component={PersonalStoreGroupDetails}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_PERSONAL_STORE_GROUPS_PATH}
          component={Settings}
        />
        <AuthenticatedRoute linkId="CLUB_SODA" path={CLUB_SODA_PATH} component={ClubSoda} />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={EDIT_OPERATING_SCHEDULES_PATH}
          component={EditOperatingSchedules}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={EDIT_LEAD_TIME_PATH}
          component={EditLeadTime}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_BULK_EDIT_LEAD_TIMES_PATH}
          component={BulkEditLeadTimes}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_BULK_EDIT_OPERATING_SCHEDULES_PATH}
          component={BulkEditOperatingSchedules}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          exact
          path={SETTINGS_BULK_EDIT_TEMPORARY_CLOSURES_PATH}
          component={BulkEditTemporaryClosures}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={SETTINGS_STORE_DETAILS_PATH}
          component={StoreDetails}
        />
        <AuthenticatedRoute
          linkId="ORDERS"
          path={QUOTE_OR_ORDER_PATH}
          component={QuoteOrOrderRedirect}
        />
        <AuthenticatedRoute
          linkId="TRAINING_RESOURCES"
          exact
          path={TRAINING_RESOURCES_PATH}
          component={TrainingResources}
        />
        <AuthenticatedRoute
          linkId="RELIABILITY_ROCKSTAR"
          exact
          path={RELIABILITY_ROCKSTAR_PATH}
          component={ReliabilityRockstar}
        />
        <AuthenticatedRoute
          linkId="OPERATIONAL_METRICS"
          exact
          path={OPERATIONAL_METRICS_PATH}
          component={OperationalMetrics}
        />
        <AuthenticatedRoute
          linkId="OPERATIONAL_METRICS"
          exact
          path={OPERATIONAL_METRICS_STORE_PATH}
          component={OperationalMetrics}
        />
        <AuthenticatedRoute
          linkId="OPERATIONAL_METRICS"
          exact
          path={OPERATIONAL_METRICS_HELP_CENTER_PATH}
          component={OperationalMetrics}
        />
        <AuthenticatedRoute linkId="MENU" path={MENU_PATH} component={Menu} />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={TAX_INFO_SETTINGS}
          component={TaxInfoSettings}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={SETTINGS_BULK_EDIT_HOLIDAY_CLOSURES_PATH}
          component={BulkEditHolidayClosures}
        />
        <AuthenticatedRoute
          linkId="SETTINGS"
          path={SETTINGS_BULK_EDIT_DAILY_CAPACITY_PATH}
          component={BulkEditDailyCapacity}
        />
        <AuthenticatedRoute linkId="SUBWAY" path={SUBWAY_URL} component={Subway} />
        <AuthenticatedRoute linkId="WORKRAMP" path={WORKRAMP_PATH} component={Workramp} />
        <EzManageRouteNoAuth
          linkId="MARK_ORDER_READY_FOR_PICKUP"
          path={[MARK_ORDER_READY_FOR_PICKUP_PATH]}
          component={MarkOrderReadyForPickup}
        />
        {authenticatedRoutes.map((routeConfig, i) => {
          return (
            <EzManageRoute
              key={i}
              {...routeConfig}
              component={routeConfig.component}
              requestUrl={requestUrl}
              loggedOutListener={loggedOutListener}
              me={data?.me}
              loading={loading}
              error={error}
            />
          );
        })}
        <RedirectRoute exact path={LEGACY_CLUB_SODA_PATH} to={CLUB_SODA_PATH} />
        <RedirectRoute exact path={DIRECT_ENTRY_PATH} to={ORDER_ENTRY_PATH} />
        <RedirectRoute exact path="/" to={ORDER_INDEX_PATH} />
        <RedirectRoute
          exact
          path={LEGACY_DRAFT_AND_QUOTE_INDEX_PATH}
          to={DRAFT_AND_QUOTE_INDEX_PATH}
        />
        <RedirectRoute exact path={MARKETING_TOOLS_PATH} to={SPONSORED_LISTING_LANDING_PATH} />
        <Route component={GeneralError} />
      </EzManageNavSwitch>
      <FullStory me={data?.me} />
    </Intercom>
  );
};

export default Routes;
